/**
 * ResultPanel — etapa 4 de "Importar base de clientes" (Item 391-C).
 *
 * Renderiza os counts do `ImportResult` (criadas/promovidas/bonds/participantes),
 * destaca `reconciliationsPending` (linhas que divergiram da base existente e
 * geraram pendências de reconciliação) e lista os `errors[]` (rowIndex + msg).
 *
 * Quando há reconciliações pendentes, oferece um link para a Inbox de pendências
 * (rota `/pendencias`), reusando a fila existente — não há fila nova.
 *
 * HONEST-DEFER: a fila de pendências não tem (ainda) um filtro de categoria
 * `reconciliation` — o filter-rail client-side só conhece as categorias
 * cadastradas. As pendências de reconciliação aparecem na lista do admin (são
 * `ownerUserId=actor`), mas sem um chip dedicado para isolá-las. O link leva à
 * fila completa; o aviso abaixo explica isso ao admin.
 */

import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { ImportResult } from '../../lib/types';

interface StatDef {
  key: keyof Pick<
    ImportResult,
    'identitiesCreated' | 'identitiesPromoted' | 'bondsCreated' | 'participantsCreated'
  >;
  label: string;
}

const STATS: StatDef[] = [
  { key: 'identitiesCreated', label: 'Identidades criadas' },
  { key: 'identitiesPromoted', label: 'Identidades promovidas' },
  { key: 'bondsCreated', label: 'Contratos criados' },
  { key: 'participantsCreated', label: 'Participantes criados' },
];

export interface ResultPanelProps {
  result: ImportResult;
  onReset: () => void;
}

export function ResultPanel({ result, onReset }: ResultPanelProps): JSX.Element {
  const hasErrors = result.errors.length > 0;

  return (
    <section className="cr-step" aria-labelledby="cr-result-title" data-testid="cr-result-panel">
      <h2 id="cr-result-title" className="cr-step__title">
        <CheckCircle2 size={18} aria-hidden style={{ marginRight: 8 }} />
        Importação concluída — {result.total} linha(s)
      </h2>

      <div className="cr-result__grid">
        {STATS.map((stat) => (
          <div key={stat.key} className="cr-result__stat">
            <div className="cr-result__stat-num" data-testid={`cr-stat-${stat.key}`}>
              {result[stat.key]}
            </div>
            <div className="cr-result__stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {result.reconciliationsPending > 0 && (
        <div className="cr-result__recon" data-testid="cr-result-recon">
          <div className="cr-result__recon-title">
            <AlertTriangle size={16} aria-hidden style={{ marginRight: 6, verticalAlign: -2 }} />
            {result.reconciliationsPending} registro(s) divergiram da base existente
          </div>
          <p className="cr-result__recon-body">
            Esses registros geraram <strong>pendências de reconciliação</strong> para revisão — a
            base existente não foi sobrescrita. Abra a fila de pendências para decidir o que fazer
            com cada divergência.
          </p>
          <p className="cr-result__recon-body" style={{ marginTop: 8 }}>
            <Link to="/pendencias" data-testid="cr-recon-link" className="cr-btn cr-btn--ghost">
              Ir para a fila de pendências
            </Link>
          </p>
          <p className="cr-result__recon-body" style={{ marginTop: 8, fontStyle: 'italic' }}>
            Obs.: a fila ainda não tem um filtro dedicado para “reconciliação”; elas aparecem na
            lista junto às demais pendências atribuídas a você.
          </p>
        </div>
      )}

      {hasErrors && (
        <div className="cr-result__errors" data-testid="cr-result-errors">
          <div className="cr-result__errors-title">
            {result.errors.length} linha(s) com erro (ignoradas)
          </div>
          <ul className="cr-result__errors-list">
            {result.errors.map((err) => (
              <li key={err.rowIndex}>
                Linha {err.rowIndex + 1}: {err.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="cr-actions">
        <button type="button" className="cr-btn cr-btn--ghost" onClick={onReset}>
          Importar outra base
        </button>
      </div>
    </section>
  );
}
